import classnames from 'classnames'
import { useTranslation } from 'next-i18next'

import { FormattedPrice } from '@src/blocks/currency/currency.component'
import { STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import productHelper, {
  getUnitForPerServing,
  shouldShowPricePerUnit,
} from '@src/quince-site-common/src/helpers/productHelper'
import { useNewPdpDesignV2Bucket } from '@src/quince-site-pdp/src/templates/productPage/hooks/useShowNewPdpDesignV2'

import { ProductInfoPriceProps } from '../productInfoPrice/ProductInfoPrice'

import styles from './MiniPDPProductInfoPrice.module.scss'

const DIFFERENCE_TO_IGNORE = 0.1

function MiniPDPProductInfoPrice({
  product,
  formattedSalePrice,
  formattedTraditionalPrice,
  savingPercentage,
  formattedFestivalOfferPrice,
  showDiscount = true,
  pricePerServing,
  traditionalPricePerServing,
  festivalPricePerUnit,
  hideSavings,
  isFinalSaleProduct,
}: ProductInfoPriceProps) {
  const { t } = useTranslation('common')
  const isDiscountLabelPresent =
    showDiscount && formattedTraditionalPrice
      ? Number(formattedTraditionalPrice) - Number(formattedSalePrice) > DIFFERENCE_TO_IGNORE
      : false

  const showPricePerUnit = shouldShowPricePerUnit(product)
  const hasPerUnitPricing = showPricePerUnit && !!pricePerServing && !!traditionalPricePerServing
  const perUnitSavingsPercentage = hasPerUnitPricing
    ? productHelper.getSavingPercentage(traditionalPricePerServing, pricePerServing)
    : ''
  const perUnitLabel = getUnitForPerServing(product)
  const unitForPerServing = perUnitLabel ? t(perUnitLabel) : ''
  const newPdpDesignV2Bucket = useNewPdpDesignV2Bucket()
  const showNewPdpDesignV2 = newPdpDesignV2Bucket !== STATSIG_EXPERIMENTS_BUCKETS.CONTROL
  const showSavingPercentage =
    !formattedFestivalOfferPrice &&
    formattedTraditionalPrice &&
    savingPercentage &&
    (isDiscountLabelPresent || perUnitSavingsPercentage) &&
    !hideSavings

  const displaySavingPercentage = hasPerUnitPricing ? perUnitSavingsPercentage : savingPercentage

  const isFestival = !!formattedFestivalOfferPrice
  const showPerUnitMainPrice = hasPerUnitPricing && !isFestival

  const getSecondPrice = () => {
    if (isFestival) return formattedFestivalOfferPrice
    if (isDiscountLabelPresent && formattedTraditionalPrice) return formattedTraditionalPrice
    return undefined
  }

  const primaryPrice = formattedSalePrice
  const secondaryPrice = getSecondPrice()
  const showSecondaryPrice = secondaryPrice && !showPerUnitMainPrice

  const firstPriceClass = !isFestival
    ? styles.priceWrapper__value
    : classnames(styles.priceWrapper__value, styles.priceWrapper__value__lineThrough)
  const secondPriceClass = !isFestival
    ? classnames(styles.priceWrapper__value__traditional, styles.priceWrapper__value__lineThrough)
    : classnames(styles.priceWrapper__value, styles.priceWrapper__value__festivalOffer)

  return (
    <div>
      <div className={classnames(styles.priceWrapper)}>
        {/* Prices */}
        <div className={firstPriceClass}>
          <FormattedPrice amount={primaryPrice} asData={false} />
        </div>
        {showSecondaryPrice && (
          <div className={secondPriceClass}>
            <FormattedPrice amount={secondaryPrice} />
          </div>
        )}
        {showPerUnitMainPrice && unitForPerServing && (
          <div>
            {'('}
            <span className={styles.priceWrapper__value__lineThrough}>
              <FormattedPrice amount={traditionalPricePerServing} asData={false} />{' '}
            </span>{' '}
            <FormattedPrice amount={pricePerServing} asData={false} /> /{unitForPerServing}
            {')'}
          </div>
        )}
        {isFinalSaleProduct && (
          <div
            className={classnames(
              styles.priceWrapper__finalSale,
              showNewPdpDesignV2 && styles.priceWrapper__finalSale_newDesignV2,
            )}
          >
            <span>{t('final_sale')} </span>
          </div>
        )}
      </div>
      {/* Savings Subtexts */}
      {showSavingPercentage && displaySavingPercentage && (
        <div className={styles.priceWrapper__subText}>
          {t('save')} <span>{displaySavingPercentage}</span>
          {t('compared_to_traditional_retail')}
        </div>
      )}
      {isFestival && !hasPerUnitPricing && isDiscountLabelPresent && formattedTraditionalPrice && (
        <div className={classnames(styles.priceWrapper__subText)}>
          {t('vs')}
          {`. `}
          <FormattedPrice amount={formattedTraditionalPrice} /> {t('traditional_retail').toLowerCase()}
        </div>
      )}
      {festivalPricePerUnit && traditionalPricePerServing && unitForPerServing && (
        <div className={classnames(styles.priceWrapper__subText)}>
          <span className={styles.priceWrapper__subText__lineThrough}>
            <FormattedPrice amount={traditionalPricePerServing} />
          </span>{' '}
          <FormattedPrice amount={festivalPricePerUnit} /> /{' '}
          {t('per_unit_vs_traditional_retail', { value: unitForPerServing })}
        </div>
      )}
    </div>
  )
}

export default MiniPDPProductInfoPrice
